import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { alpha } from "@mui/material/styles";
import { API_BASE_URL } from "../../config";
import { resolveGate0 } from "../../utils/gate0Storage";
import { createItem, createSection } from "../framework/frameworkUtils";
import { getPresetFramework } from "../framework/presets";

const DEPTH_OPTIONS = [
  { key: "lite", label: "Lightweight", hint: "4 sections — pilots, internal tools" },
  { key: "standard", label: "Standard", hint: "6 sections — balanced coverage" },
  { key: "enterprise", label: "Enterprise", hint: "7 sections — full depth" },
  { key: "regulated", label: "Regulated", hint: "9 sections — audit & evidence" },
  { key: "healthcare", label: "Healthcare", hint: "8 sections — clinical domain" },
  { key: "defaults", label: "Default Sections", hint: "Use the standard Gate 0 sections" },
  { key: "manual", label: "Set my own", hint: "Choose section count (1–20)" },
];

const CAPABILITIES = [
  "Conversational chat",
  "Retrieval / RAG",
  "Agentic / tool use",
  "Voice",
  "Document processing",
  "Summarisation",
  "Classification / routing",
  "Recommendation",
];

const requiredMarkSx = { color: "#DE350B", ml: 0.25 };

const PERMISSION_OPTIONS = [
  { value: "answer_only", label: "Answer only", desc: "Structure locked — intake chat only" },
  { value: "answer_and_comment", label: "Answer and comment", desc: "Structure locked — can flag questions (comments coming soon)" },
  { value: "answer_and_edit", label: "Answer and edit", desc: "EAO can add or reword sections and questions" },
];

function isRequiredQuestion(q) {
  return q?.required !== false;
}

function hasSelectedCapability(capabilities) {
  return Array.isArray(capabilities) && capabilities.some((cap) => String(cap || "").trim());
}

const CAPABILITY_REQUIRED_MESSAGE = "Please select at least one capability to generate sections.";

function ensureFramework(base) {
  if (base && Array.isArray(base.sections)) return base;
  return {
    name: "Tenant onboarding template",
    version: 2,
    mandatory_defaults_version: 3,
    passing_mode: "weighted",
    default_threshold: 85,
    generated: false,
    sections: [],
  };
}

function isUsableApplicationFramework(framework) {
  const sections = framework?.sections;
  if (!Array.isArray(sections) || sections.length === 0) return false;
  return sections.some((section) => Array.isArray(section?.items) && section.items.length > 0);
}

function cloneFramework(framework) {
  return JSON.parse(JSON.stringify(framework));
}

function countLabel(count, singular, plural) {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? singular : plural}`;
}

function ApplicationTemplateLibrary({
  templates,
  error,
  applyingId,
  preview,
  onBack,
  onView,
  onClosePreview,
  onUse,
  onScratch,
  onDismissError,
}) {
  return (
    <Box sx={{ bgcolor: "#f2f5f8", minHeight: "100vh", pb: 6 }}>
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #dde4ec", px: 3, py: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={700} fontSize="15px">
            Application onboarding template
          </Typography>
          <Typography fontSize="12px" color="text.secondary">
            Choose a starting skeleton
          </Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 840, mx: "auto", px: 3, py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={onDismissError}>
            {error}
          </Alert>
        )}

        <Typography fontSize="11px" letterSpacing="0.16em" textTransform="uppercase" color="text.secondary" mb={1}>
          Existing Templates
        </Typography>
        <Typography variant="h6" fontWeight={600} mb={0.5}>
          Start from a template in this organisation
        </Typography>
        <Typography fontSize="14px" color="text.secondary" mb={3}>
          Published templates from other applications in this tenant can be copied as a starting point.
          Editing the copy does not change the original application.
        </Typography>

        {templates.length === 0 && !error && (
          <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 2 }}>
            <Typography fontSize="14px" color="text.secondary">
              No previous customized templates are available for this tenant.
            </Typography>
          </Paper>
        )}

        {templates.map((item) => (
          <Paper
            key={item.project_id}
            variant="outlined"
            sx={{
              p: 2,
              mb: 1.5,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography fontWeight={600} fontSize="15px">
                {item.project_name}
              </Typography>
              <Typography fontSize="13px" color="text.secondary" mt={0.25}>
                {countLabel(item.section_count, "section", "sections")}
                {" • "}
                {countLabel(item.question_count, "question", "questions")}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => onView(item)}
              disabled={Boolean(applyingId)}
              sx={{ textTransform: "none" }}
            >
              View Template
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => onUse(item)}
              disabled={Boolean(applyingId)}
              sx={{ textTransform: "none" }}
            >
              {applyingId === item.project_id ? "Copying…" : "Use Template"}
            </Button>
          </Paper>
        ))}

        <Button
          variant="outlined"
          onClick={onScratch}
          disabled={Boolean(applyingId)}
          sx={{ mt: 1, textTransform: "none" }}
        >
          Start from Scratch
        </Button>
      </Box>

      <Dialog open={Boolean(preview)} onClose={onClosePreview} fullWidth maxWidth="md">
        <DialogTitle>
          {preview?.project_name || "Template"}
          <Typography fontSize="13px" color="text.secondary" mt={0.5}>
            Read-only preview
            {preview
              ? ` · ${countLabel(preview.section_count, "section", "sections")} • ${countLabel(preview.question_count, "question", "questions")}`
              : ""}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {(preview?.framework?.sections || []).map((section, index) => (
            <Box key={section.id || index} sx={{ mb: 2.5 }}>
              <Typography fontWeight={600} fontSize="15px">
                {section.name || `Section ${index + 1}`}
              </Typography>
              {section.purpose && (
                <Typography fontSize="13px" color="text.secondary" mt={0.25}>
                  {section.purpose}
                </Typography>
              )}
              <Typography fontSize="11px" color="text.secondary" fontFamily="monospace" mt={0.5}>
                {section.required === false ? "section optional" : "section required"}
                {" · "}
                {countLabel(section.items?.length || 0, "question", "questions")}
              </Typography>
              {(section.items || []).length === 0 && (
                <Typography fontSize="13px" color="text.secondary" fontStyle="italic" mt={1}>
                  No questions.
                </Typography>
              )}
              {(section.items || []).map((question, qi) => (
                <Box
                  key={question.id || qi}
                  sx={{
                    mt: 1,
                    py: 1,
                    px: 1.5,
                    borderRadius: 1,
                    bgcolor: "#f9fafb",
                    border: "1px solid #eef1f4",
                  }}
                >
                  <Typography fontSize="14px">{question.text}</Typography>
                  <Typography fontSize="11px" color="text.secondary" fontFamily="monospace" mt={0.25}>
                    {question.required === false ? "optional" : "required"}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClosePreview} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SectionBlock({
  section,
  index,
  open,
  onToggle,
  onEditSection,
  onDeleteSection,
  onEditQuestion,
  onDeleteQuestion,
  onAddQuestion,
}) {
  const qCount = section.items?.length || 0;
  return (
    <Paper variant="outlined" sx={{ mb: 1, borderRadius: 2, overflow: "hidden" }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          "&:hover": { bgcolor: "#fafcfd" },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={onToggle}>
          <Typography fontWeight={600} fontSize="15px">
            {section.name}
          </Typography>
          {section.purpose && (
            <Typography fontSize="13px" color="text.secondary" mt={0.25}>
              {section.purpose}
            </Typography>
          )}
        </Box>
        <Chip label={`${qCount} Q`} size="small" sx={{ fontFamily: "monospace", fontSize: 11 }} />
        <Box sx={{ display: "flex", gap: 0.25 }}>
          <IconButton size="small" title="Edit section" onClick={onEditSection}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" title="Remove section" onClick={onDeleteSection}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onToggle}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2, pt: 0 }}>
          {(section.items || []).length === 0 && (
            <Typography fontSize="13px" color="text.secondary" fontStyle="italic" py={1}>
              No questions yet.
            </Typography>
          )}
          {(section.items || []).map((q, qi) => (
            <Box
              key={q.id || qi}
              sx={{
                py: 1,
                px: 1.5,
                mb: 0.5,
                borderRadius: 1,
                bgcolor: "#f9fafb",
                border: "1px solid #eef1f4",
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontSize="14px">{q.text}</Typography>
                <Typography fontSize="11px" color="text.secondary" fontFamily="monospace" mt={0.25}>
                  {q.required ? "required" : "optional"}
                </Typography>
              </Box>
              <IconButton size="small" title="Edit question" onClick={() => onEditQuestion(qi)}>
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                title={
                  isRequiredQuestion(q)
                    ? "Required questions cannot be removed"
                    : "Remove question"
                }
                onClick={() => onDeleteQuestion(qi)}
                disabled={isRequiredQuestion(q)}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddQuestion}
            sx={{ mt: 1, textTransform: "none" }}
          >
            Add question
          </Button>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function TenantTemplateConfigPage() {
  const { id, projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isApplication = Boolean(projectId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [warning, setWarning] = useState("");

  const [tenant, setTenant] = useState(null);
  const [appBrief, setAppBrief] = useState("");
  const [depth, setDepth] = useState("enterprise");
  const [manualCount, setManualCount] = useState(7);
  const [capabilities, setCapabilities] = useState(["Conversational chat", "Retrieval / RAG"]);
  const [permissions, setPermissions] = useState("answer_and_edit");
  const [templateStatus, setTemplateStatus] = useState("draft");
  const [framework, setFramework] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [openSections, setOpenSections] = useState({});

  const [sectionDialog, setSectionDialog] = useState(null);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [questionDialog, setQuestionDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [similarityDialog, setSimilarityDialog] = useState(null);
  const [similarityLoading, setSimilarityLoading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [reusableTemplates, setReusableTemplates] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [applyingTemplateId, setApplyingTemplateId] = useState(null);

  const baseUrl = isApplication
    ? `${API_BASE_URL}/projects/${projectId}/onboarding-template`
    : `${API_BASE_URL}/superadmin/tenants/${id}/onboarding-template`;

  const backPath = isApplication
    ? `/framework/${projectId}`
    : `/platform/tenants/${id}`;

  const formatMatchLine = (m) => {
    const where = m.section_name ? `§ ${m.section_name}` : "";
    const label = m.type === "section" ? "Section" : "Question";
    return `${label} (${where}): ${m.text}`;
  };

  const formatConflictLine = (c) => {
    const aLabel = c.a?.type === "section" ? "Section" : "Question";
    const bLabel = c.b?.type === "section" ? "Section" : "Question";
    return `${aLabel} “${c.a?.text}” ↔ ${bLabel} “${c.b?.text}” (${c.score})`;
  };

  /** @returns {Promise<{ ok: boolean, severity: string, matches?: any[], conflicts?: any[] }|null>} */
  const runCandidateCheck = async (candidate) => {
    const res = await axios.post(`${baseUrl}/similarity-check`, {
      mode: "candidate",
      framework: framework || { sections: [] },
      candidate,
    });
    return res.data?.data || null;
  };

  /** AI quality / section-fit gate (runs before duplicate detection). */
  const runQuestionValidation = async ({ text, sectionIndex }) => {
    const res = await axios.post(`${baseUrl}/validate`, {
      kind: "question",
      framework: framework || { sections: [] },
      candidate: {
        text,
        section_index: sectionIndex,
      },
    });
    return res.data?.data || null;
  };

  /** Section title/purpose shape gate (before Generate Questions and Save & Close). */
  const runSectionValidation = async ({ name, purpose }) => {
    const res = await axios.post(`${baseUrl}/validate`, {
      kind: "section",
      section_name: name,
      section_purpose: purpose || "",
    });
    return res.data?.data || null;
  };

  const showSimilarityPrompt = ({
    title,
    severity,
    message,
    matches = [],
    conflicts = [],
    allowOverride = true,
    proceedLabel,
    onProceed,
  }) => {
    setSimilarityDialog({
      title,
      severity,
      message,
      matches,
      conflicts,
      allowOverride,
      proceedLabel,
      onProceed,
    });
  };

  const handleSimilarityConflictError = (err, { title, onOverride }) => {
    const data = err.response?.data;
    if (err.response?.status === 409 && data?.code === "SIMILARITY_CONFLICT") {
      showSimilarityPrompt({
        title: title || "Similar content detected",
        severity: data.severity || "block",
        matches: data.matches || [],
        conflicts: data.conflicts || [],
        allowOverride: true,
        onProceed: onOverride,
      });
      return true;
    }
    return false;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(baseUrl);
      const data = res.data?.data;
      const fw = data?.framework ? ensureFramework(data.framework) : null;
      setTenant(data);
      setFramework(fw);
      setIsDirty(false);
      setTemplateStatus(data?.template_status || "draft");
      setPermissions(data?.template_permissions || "answer_and_edit");
      const meta = data?.meta || {};
      if (meta.app_brief) setAppBrief(meta.app_brief);
      if (meta.depth) setDepth(meta.depth);
      if (meta.section_count && meta.depth === "manual") setManualCount(meta.section_count);
      if (Array.isArray(meta.capabilities) && meta.capabilities.length) {
        setCapabilities(meta.capabilities);
      }
      if (isApplication && !meta.app_brief) {
        const gate0 = resolveGate0(projectId, location.state);
        const brief = String(gate0?.intended_use || "").trim();
        if (brief.length >= 20) setAppBrief(brief);
      }
      const needsLibrary = isApplication && !isUsableApplicationFramework(fw);
      if (needsLibrary) {
        try {
          const libRes = await axios.get(`${API_BASE_URL}/projects/templates/reusable`, {
            params: { excludeProjectId: projectId },
          });
          setReusableTemplates(Array.isArray(libRes.data?.data) ? libRes.data.data : []);
        } catch (libErr) {
          setReusableTemplates([]);
          setError(libErr.response?.data?.error || "Could not load existing templates.");
        }
        setShowLibrary(true);
      } else {
        setReusableTemplates([]);
        setShowLibrary(false);
      }
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load template.");
    } finally {
      setLoading(false);
    }
  }, [baseUrl, isApplication, projectId, location.state]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const totals = useMemo(() => {
    const sections = framework?.sections || [];
    const questions = sections.reduce((n, s) => n + (s.items?.length || 0), 0);
    return { sections: sections.length, questions };
  }, [framework]);

  const updateFrameworkSections = (updater) => {
    setFramework((prev) => {
      const base = ensureFramework(prev);
      const sections = updater(base.sections || []);
      return { ...base, sections };
    });
    setIsDirty(true);
  };

  const toggleCap = (cap) => {
    setCapabilities((prev) =>
      prev.includes(cap) ? prev.filter((c) => c !== cap) : [...prev, cap]
    );
  };

  const applyDefaultSections = () => {
    const preset = getPresetFramework("standard");
    setFramework(preset);
    setIsDirty(true);
    const open = {};
    (preset?.sections || []).forEach((s, i) => { open[i] = i === 0; });
    setOpenSections(open);
  };

  const handleGenerate = async () => {
    if (depth === "defaults") return;
    if (!hasSelectedCapability(capabilities)) return;
    if (appBrief.trim().length < 20) {
      setError("Describe the application in at least 20 characters.");
      return;
    }
    setGenerating(true);
    setError("");
    setSuccess("");
    try {
      const res = await axios.post(`${baseUrl}/generate`, {
        app_brief: appBrief.trim(),
        depth,
        manual_section_count: depth === "manual" ? manualCount : undefined,
        capabilities,
      });
      const data = res.data?.data;
      setFramework(ensureFramework(data?.framework));
      setIsDirty(false);
      setTemplateStatus(data?.template_status || "draft");
      const open = {};
      (data?.framework?.sections || []).forEach((s, i) => { open[i] = i === 0; });
      setOpenSections(open);

      const similarity = res.data?.similarity;
      if (similarity && !similarity.ok) {
        showSimilarityPrompt({
          title: "Generated template has similar sections/questions",
          severity: similarity.severity,
          conflicts: similarity.conflicts || [],
          allowOverride: false,
          onProceed: null,
        });
        setSuccess("Sections generated — review similar items below before publishing.");
      } else {
        setSuccess("Sections generated — review below, then publish when ready.");
      }
    } catch (e) {
      setError(e.response?.data?.error || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const persistDraft = async ({ allowOverride = false } = {}) => {
    const body = {
      framework,
      meta: { app_brief: appBrief.trim(), depth, capabilities },
      allow_similarity_override: allowOverride,
    };
    if (!isApplication) {
      body.template_permissions = permissions;
    }
    await axios.put(baseUrl, body);
  };

  const handlePublish = async ({ allowOverride = false } = {}) => {
    if (!framework?.sections?.length) {
      setError("Generate a template first.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await persistDraft({ allowOverride });
      const publishBody = {
        template_status: "published",
        framework,
        allow_similarity_override: allowOverride,
      };
      if (!isApplication) {
        publishBody.template_permissions = permissions;
      }
      const res = await axios.put(`${baseUrl}/publish`, publishBody);
      const data = res.data?.data;
      setTemplateStatus(data?.template_status || "published");
      setIsDirty(false);
      setSimilarityDialog(null);
      setSuccess(
        isApplication
          ? `Published to ${tenant?.org_name || "this application"}. Configuration and intake will use this template.`
          : `Published to ${tenant?.org_name || "tenant"}. New apps will inherit this template.`
      );
    } catch (e) {
      if (
        handleSimilarityConflictError(e, {
          title: "Cannot publish — similar content found",
          onOverride: () => handlePublish({ allowOverride: true }),
        })
      ) {
        return;
      }
      setError(e.response?.data?.error || "Publish failed.");
    } finally {
      setSaving(false);
    }
  };

  const enterEditorWithFramework = (fw, { dirty, status, message } = {}) => {
    const next = ensureFramework(fw);
    setFramework(next);
    setIsDirty(Boolean(dirty));
    if (status) setTemplateStatus(status);
    setShowLibrary(false);
    setPreviewTemplate(null);
    const open = {};
    (next.sections || []).forEach((s, i) => { open[i] = i === 0; });
    setOpenSections(open);
    if (message) setSuccess(message);
  };

  const handleUseTemplate = async (item, { allowOverride = false } = {}) => {
    if (!item?.framework) {
      setError("This template has no framework to copy.");
      return;
    }
    const clone = cloneFramework(item.framework);
    setApplyingTemplateId(item.project_id);
    setError("");
    try {
      const res = await axios.put(baseUrl, {
        framework: clone,
        source_project_id: item.project_id,
        allow_similarity_override: allowOverride,
      });
      const data = res.data?.data;
      if (data) setTenant(data);
      enterEditorWithFramework(data?.framework || clone, {
        dirty: false,
        status: data?.template_status || "draft",
        message: `Copied “${item.project_name}” into this application. The original template is unchanged.`,
      });
      setSimilarityDialog(null);
    } catch (e) {
      if (
        handleSimilarityConflictError(e, {
          title: "Similar content in this template",
          onOverride: () => handleUseTemplate(item, { allowOverride: true }),
        })
      ) {
        enterEditorWithFramework(clone, { dirty: true, status: "draft" });
        return;
      }
      setError(e.response?.data?.error || "Could not use this template.");
    } finally {
      setApplyingTemplateId(null);
    }
  };

  const handleStartFromScratch = () => {
    setShowLibrary(false);
    setPreviewTemplate(null);
    setError("");
  };

  const handleSaveDraft = async ({ allowOverride = false } = {}) => {
    if (!framework) return;
    setSaving(true);
    setError("");
    try {
      await persistDraft({ allowOverride });
      setIsDirty(false);
      setSimilarityDialog(null);
      setSuccess("Draft saved.");
    } catch (e) {
      if (
        handleSimilarityConflictError(e, {
          title: "Near-duplicate content — save blocked",
          onOverride: () => handleSaveDraft({ allowOverride: true }),
        })
      ) {
        return;
      }
      setError(e.response?.data?.error || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const openAddSection = () => {
    setError("");
    setSectionDialog({ mode: "add", name: "", purpose: "", items: [] });
  };

  const openEditSection = (index) => {
    const sec = framework?.sections?.[index];
    if (!sec) return;
    setSectionDialog({ mode: "edit", index, name: sec.name || "", purpose: sec.purpose || "" });
  };

  /** One-shot Generate Questions (Add Section) — same redraft endpoint, no instruction dialog. */
  const handleGenerateQuestions = async () => {
    if (depth === "defaults") return;
    const name = sectionDialog?.name?.trim();
    if (!name || sectionDialog?.mode !== "add") return;
    const purpose = sectionDialog?.purpose?.trim() || "";

    setGeneratingQuestions(true);
    setError("");
    try {
      const validation = await runSectionValidation({ name, purpose });
      if (validation && validation.ok === false && validation.severity === "error") {
        setError(
          validation.checks?.quality?.reason
            || "This does not appear to be a meaningful section title."
        );
        return;
      }

      const res = await axios.post(`${baseUrl}/redraft`, {
        kind: "new_questions",
        section_name: name,
        section_purpose: purpose,
        framework: framework || { sections: [] },
        app_brief: appBrief.trim(),
        capabilities,
      });
      const questions = res.data?.data?.draft?.questions;
      if (!Array.isArray(questions) || !questions.length) {
        throw new Error("No questions returned.");
      }
      setSectionDialog((d) => ({
        ...d,
        items: questions.map((q, i) => ({
          id: `draft_q_${Date.now()}_${i}`,
          text: String(q.text || "").trim(),
          required: q.required !== false,
        })).filter((q) => q.text),
      }));
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to generate questions.");
    } finally {
      setGeneratingQuestions(false);
    }
  };

  const addDialogQuestion = () => {
    setSectionDialog((d) => ({
      ...d,
      items: [
        ...(d.items || []),
        { id: `draft_q_${Date.now()}`, text: "", required: true },
      ],
    }));
  };

  const updateDialogQuestion = (index, patch) => {
    setSectionDialog((d) => ({
      ...d,
      items: (d.items || []).map((q, i) => (i === index ? { ...q, ...patch } : q)),
    }));
  };

  const removeDialogQuestion = (index) => {
    setSectionDialog((d) => {
      const q = d?.items?.[index];
      if (isRequiredQuestion(q)) return d;
      return {
        ...d,
        items: (d.items || []).filter((_, i) => i !== index),
      };
    });
  };

  const applySectionDialog = () => {
    const name = sectionDialog?.name?.trim();
    if (!name) return;
    const purpose = sectionDialog?.purpose?.trim() || "";
    if (sectionDialog.mode === "add") {
      updateFrameworkSections((sections) => {
        const sec = createSection(sections);
        const items = [];
        for (const q of sectionDialog.items || []) {
          const text = String(q.text || "").trim();
          if (!text) continue;
          const item = createItem({ ...sec, items });
          items.push({
            ...item,
            text,
            required: q.required !== false,
          });
        }
        return [
          ...sections,
          { ...sec, name, purpose, items, items_count: items.length },
        ];
      });
      setOpenSections((o) => ({ ...o, [framework?.sections?.length || 0]: true }));
      setSuccess(`Added section "${name}".`);
    } else {
      const idx = sectionDialog.index;
      updateFrameworkSections((sections) =>
        sections.map((s, i) => (i === idx ? { ...s, name, purpose } : s))
      );
      setSuccess("Section updated.");
    }
    setSectionDialog(null);
    setSimilarityDialog(null);
  };

  const saveSectionDialog = async () => {
    const name = sectionDialog?.name?.trim();
    if (!name) return;
    const purpose = sectionDialog?.purpose?.trim() || "";
    const excludeSectionId =
      sectionDialog.mode === "edit"
        ? framework?.sections?.[sectionDialog.index]?.id
        : undefined;

    setSimilarityLoading(true);
    setError("");
    try {
      const validation = await runSectionValidation({ name, purpose });
      if (validation && validation.ok === false && validation.severity === "error") {
        setError(
          validation.checks?.quality?.reason
            || "This does not appear to be a meaningful section title."
        );
        return;
      }

      const result = await runCandidateCheck({
        kind: "section",
        name,
        purpose,
        exclude_section_id: excludeSectionId,
      });
      if (result && !result.ok) {
        showSimilarityPrompt({
          title: "This section looks similar to an existing one",
          severity: result.severity,
          matches: result.matches || [],
          allowOverride: true,
          onProceed: applySectionDialog,
        });
        return;
      }
      applySectionDialog();
    } catch (e) {
      setError(e.response?.data?.error || "Section validation or similarity check failed.");
    } finally {
      setSimilarityLoading(false);
    }
  };

  const confirmDeleteSection = (index) => {
    const sec = framework?.sections?.[index];
    setDeleteDialog({
      title: "Remove section?",
      message: `Remove "${sec?.name || "this section"}" and all its questions?`,
      onConfirm: () => {
        updateFrameworkSections((sections) => sections.filter((_, i) => i !== index));
        setDeleteDialog(null);
        setSuccess("Section removed.");
      },
    });
  };

  const openAddQuestion = (sectionIndex) => {
    setQuestionDialog({
      mode: "add",
      sectionIndex,
      text: "",
      required: true,
    });
  };

  const openEditQuestion = (sectionIndex, questionIndex) => {
    const q = framework?.sections?.[sectionIndex]?.items?.[questionIndex];
    if (!q) return;
    setQuestionDialog({
      mode: "edit",
      sectionIndex,
      questionIndex,
      itemId: q.id != null && String(q.id) !== "" ? String(q.id) : null,
      text: q.text || "",
      required: q.required !== false,
    });
  };

  const applyQuestionDialog = (opts = {}) => {
    const text = questionDialog?.text?.trim();
    if (!text) return;
    const { sectionIndex, questionIndex, mode, required } = questionDialog;
    updateFrameworkSections((sections) =>
      sections.map((s, si) => {
        if (si !== sectionIndex) return s;
        if (mode === "add") {
          const item = createItem(s);
          return {
            ...s,
            items: [...(s.items || []), { ...item, text, required }],
            items_count: (s.items?.length || 0) + 1,
          };
        }
        const items = (s.items || []).map((it, qi) =>
          qi === questionIndex ? { ...it, text, required } : it
        );
        return { ...s, items };
      })
    );
    setQuestionDialog(null);
    setSimilarityDialog(null);
    if (opts.semanticUnavailable) {
      const action = mode === "edit" ? "updated" : "added";
      setWarning(
        opts.unavailableMessage
          || `Semantic validation is currently unavailable. The question was ${action} without duplicate checking.`
      );
    } else {
      setSuccess(mode === "add" ? "Question added." : "Question updated.");
    }
  };

  /** Existing near-duplicate gate — called after AI validation passes or is overridden. */
  const runQuestionDuplicateGate = async ({ validationUnavailable = false } = {}) => {
    const text = questionDialog?.text?.trim();
    if (!text) return;
    const { sectionIndex, questionIndex, mode, itemId } = questionDialog;
    const liveSection = framework?.sections?.[sectionIndex];
    const liveItem = liveSection?.items?.[questionIndex];
    const excludeItemId =
      mode === "edit"
        ? (itemId
            || (liveItem?.id != null && String(liveItem.id) !== ""
              ? String(liveItem.id)
              : null))
        : null;

    setSimilarityLoading(true);
    setError("");
    try {
      const candidate = {
        kind: "question",
        text,
      };
      if (mode === "edit") {
        if (excludeItemId) candidate.exclude_item_id = excludeItemId;
        if (liveSection?.id != null && String(liveSection.id) !== "") {
          candidate.exclude_section_id = String(liveSection.id);
        }
        // Index fallback so the edited row is excluded even if id is missing/mismatched.
        candidate.exclude_section_index = sectionIndex;
        candidate.exclude_question_index = questionIndex;
      }

      const result = await runCandidateCheck(candidate);
      if (result && !result.ok) {
        const sev = result.severity;
        if (sev === "hard_duplicate") {
          showSimilarityPrompt({
            title: "Hard duplicate question detected",
            severity: "hard_duplicate",
            message:
              "This question is nearly identical to an existing one and cannot be added as written. Rewrite the question to continue.",
            matches: result.matches || [],
            allowOverride: false,
          });
          return;
        }
        if (sev === "duplicate") {
          showSimilarityPrompt({
            title: "Duplicate question detected",
            severity: "duplicate",
            message:
              "This question matches an existing one closely enough to be treated as a duplicate. Cancel to rewrite, or keep anyway if intentional.",
            matches: result.matches || [],
            allowOverride: true,
            proceedLabel: "Keep anyway",
            onProceed: () => applyQuestionDialog({
              semanticUnavailable: validationUnavailable || Boolean(result?.validation_unavailable),
              unavailableMessage: validationUnavailable
                ? `Semantic validation is currently unavailable. The question was ${mode === "edit" ? "updated" : "added"} without full validation.`
                : undefined,
            }),
          });
          return;
        }
        showSimilarityPrompt({
          title: "This question looks similar to an existing one",
          severity: sev || "warn",
          matches: result.matches || [],
          allowOverride: true,
          proceedLabel: "Keep anyway",
          onProceed: () => applyQuestionDialog({
            semanticUnavailable: validationUnavailable || Boolean(result?.validation_unavailable),
            unavailableMessage: validationUnavailable
              ? `Semantic validation is currently unavailable. The question was ${mode === "edit" ? "updated" : "added"} without full validation.`
              : undefined,
          }),
        });
        return;
      }
      applyQuestionDialog({
        semanticUnavailable: validationUnavailable || Boolean(result?.validation_unavailable),
        unavailableMessage: validationUnavailable && !result?.validation_unavailable
          ? `Semantic validation is currently unavailable. The question was ${mode === "edit" ? "updated" : "added"} without full quality/section checking.`
          : undefined,
      });
    } catch (e) {
      setError(e.response?.data?.error || "Similarity check failed.");
    } finally {
      setSimilarityLoading(false);
    }
  };

  const saveQuestionDialog = async () => {
    const text = questionDialog?.text?.trim();
    if (!text) return;
    const { sectionIndex, mode } = questionDialog;

    setSimilarityLoading(true);
    setError("");
    try {
      const validation = await runQuestionValidation({ text, sectionIndex });
      const severity = validation?.severity || (validation?.ok ? "ok" : "error");
      const checks = validation?.checks || {};
      const validationUnavailable = Boolean(validation?.validation_unavailable);

      if (severity === "error") {
        const reason =
          checks.quality?.reason ||
          "This doesn't appear to be a meaningful onboarding question.";
        showSimilarityPrompt({
          title: "Invalid question",
          severity: "error",
          message: reason,
          allowOverride: false,
        });
        return;
      }

      if (severity === "warn") {
        const fit = checks.section_fit || {};
        let reason =
          fit.reason ||
          "This question appears to belong under a different section.";
        if (fit.suggested_section && !reason.includes(fit.suggested_section)) {
          reason = `${reason} Suggested section: ${fit.suggested_section}.`;
        }
        showSimilarityPrompt({
          title: "Question may belong in another section",
          severity: "warn",
          message: reason,
          allowOverride: true,
          proceedLabel: mode === "edit" ? "Save anyway" : "Add Anyway",
          onProceed: () => {
            void runQuestionDuplicateGate({ validationUnavailable });
          },
        });
        return;
      }

      await runQuestionDuplicateGate({ validationUnavailable });
    } catch (e) {
      setError(e.response?.data?.error || "Question validation failed.");
    } finally {
      setSimilarityLoading(false);
    }
  };

  const confirmDeleteQuestion = (sectionIndex, questionIndex) => {
    const q = framework?.sections?.[sectionIndex]?.items?.[questionIndex];
    if (isRequiredQuestion(q)) {
      setError("Required questions cannot be removed. Edit the question and turn off Required to delete it.");
      return;
    }
    setDeleteDialog({
      title: "Remove question?",
      message: "This question will be removed from the template.",
      onConfirm: () => {
        const current = framework?.sections?.[sectionIndex]?.items?.[questionIndex];
        if (isRequiredQuestion(current)) {
          setDeleteDialog(null);
          return;
        }
        updateFrameworkSections((sections) =>
          sections.map((s, si) => {
            if (si !== sectionIndex) return s;
            const items = (s.items || []).filter((_, qi) => qi !== questionIndex);
            return { ...s, items, items_count: items.length };
          })
        );
        setDeleteDialog(null);
        setSuccess("Question removed.");
      },
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isApplication && showLibrary) {
    return (
      <ApplicationTemplateLibrary
        templates={reusableTemplates}
        error={error}
        applyingId={applyingTemplateId}
        preview={previewTemplate}
        onBack={() => navigate(backPath, { state: location.state })}
        onView={setPreviewTemplate}
        onClosePreview={() => setPreviewTemplate(null)}
        onUse={(item) => { void handleUseTemplate(item); }}
        onScratch={handleStartFromScratch}
        onDismissError={() => setError("")}
      />
    );
  }

  return (
    <Box sx={{ bgcolor: "#f2f5f8", minHeight: "100vh", pb: 10 }}>
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #dde4ec", px: 3, py: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton size="small" onClick={() => navigate(backPath, isApplication ? { state: location.state } : undefined)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={700} fontSize="15px">
            {isApplication ? "Application onboarding template" : "Onboarding template"}
            {" — "}
            {tenant?.org_name || (isApplication ? "Application" : "Tenant")}
          </Typography>
          <Typography fontSize="12px" color="text.secondary" fontFamily="monospace">
            {isApplication ? `App #${projectId}` : tenant?.slug}
            {" · "}
            {templateStatus === "published" ? "PUBLISHED" : "DRAFT"}
            {totals.sections > 0 && ` · ${totals.sections} sections · ${totals.questions} questions`}
          </Typography>
        </Box>
        <Button
          size="small"
          variant={isDirty ? "contained" : "outlined"}
          color="primary"
          onClick={handleSaveDraft}
          disabled={saving || !framework}
        >
          Save draft
        </Button>
      </Box>

      <Box sx={{ maxWidth: 840, mx: "auto", px: 3, py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

        <Typography fontSize="11px" letterSpacing="0.16em" textTransform="uppercase" color="text.secondary" mb={1}>
          Step 1
        </Typography>
        <Typography variant="h6" fontWeight={600} mb={0.5}>
          {isApplication
            ? "Describe this application"
            : "Describe the tenant\u2019s typical applications"}
          <Box component="span" sx={requiredMarkSx}>
            *
          </Box>
        </Typography>
        <Typography fontSize="14px" color="text.secondary" mb={2}>
          {isApplication
            ? "What this AI application does, who uses it, and what must never go wrong. This seeds AI-generated sections."
            : "What their AI apps do, who uses them, and what must never go wrong. This seeds AI-generated sections."}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={5}
          value={appBrief}
          onChange={(e) => setAppBrief(e.target.value)}
          placeholder="e.g. Retail lending assistants for branch staff — RAG over policy handbooks, must cite sources, no PII exposure..."
          inputProps={{ "aria-required": true }}
          sx={{ mb: 4, bgcolor: "#fff" }}
        />

        <Typography fontSize="11px" letterSpacing="0.16em" textTransform="uppercase" color="text.secondary" mb={1}>
          Step 2
        </Typography>
        <Typography variant="h6" fontWeight={600} mb={0.5}>
          How deep should it go?
          <Box component="span" sx={requiredMarkSx}>
            *
          </Box>
        </Typography>
        <Typography fontSize="14px" color="text.secondary" mb={2}>
          Uses existing presets as AI depth hints (Lightweight, Standard, Enterprise, etc.).
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 1, mb: depth === "manual" ? 1 : 4 }}>
          {DEPTH_OPTIONS.map((opt) => (
            <Paper
              key={opt.key}
              variant="outlined"
              onClick={() => {
                setDepth(opt.key);
                if (opt.key === "defaults") applyDefaultSections();
              }}
              sx={{
                p: 1.5,
                cursor: "pointer",
                borderColor: depth === opt.key ? "primary.main" : "#dde4ec",
                bgcolor: depth === opt.key ? (theme) => alpha(theme.palette.primary.main, 0.08) : "#fff",
                boxShadow: depth === opt.key ? (theme) => `inset 0 0 0 1px ${theme.palette.primary.main}` : "none",
              }}
            >
              <Typography fontWeight={600} fontSize="14px" color={depth === opt.key ? "primary.dark" : "inherit"}>
                {opt.label}
              </Typography>
              <Typography fontSize="12px" color="text.secondary">{opt.hint}</Typography>
            </Paper>
          ))}
        </Box>
        {depth === "manual" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
            <Typography fontSize="14px" color="text.secondary">How many sections?</Typography>
            <TextField
              type="number"
              size="small"
              inputProps={{ min: 1, max: 20 }}
              value={manualCount}
              onChange={(e) => setManualCount(Number(e.target.value))}
              sx={{ width: 80, bgcolor: "#fff" }}
            />
          </Box>
        )}

        <Typography fontSize="11px" letterSpacing="0.16em" textTransform="uppercase" color="text.secondary" mb={1}>
          Step 3
        </Typography>
        <Typography variant="h6" fontWeight={600} mb={0.5}>
          What is it made of?
          <Box component="span" sx={requiredMarkSx}>
            *
          </Box>
        </Typography>
        <Typography fontSize="14px" color="text.secondary" mb={2}>Select capabilities that apply.</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {CAPABILITIES.map((cap) => (
            <Chip
              key={cap}
              label={cap}
              onClick={() => toggleCap(cap)}
              color={capabilities.includes(cap) ? "primary" : "default"}
              variant={capabilities.includes(cap) ? "filled" : "outlined"}
              sx={{ cursor: "pointer" }}
            />
          ))}
        </Box>
        {!hasSelectedCapability(capabilities) && (
          <Typography
            id="generate-sections-capability-hint"
            component="p"
            fontSize="13px"
            color="error"
            role="status"
            sx={{ mb: 1.5 }}
          >
            {CAPABILITY_REQUIRED_MESSAGE}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
          onClick={handleGenerate}
          disabled={generating || depth === "defaults" || !hasSelectedCapability(capabilities)}
          aria-describedby={!hasSelectedCapability(capabilities) ? "generate-sections-capability-hint" : undefined}
          sx={{ textTransform: "none", mb: 1 }}
        >
          {generating ? "Generating…" : "Generate sections"}
        </Button>
        <Typography fontSize="12px" color="text.secondary" display="block" mb={4}>
          Draft only — edit, add or remove anything afterwards.
        </Typography>

        {(framework?.sections?.length > 0 || framework) && (
          <>
            <Box sx={{ borderTop: "1px solid #dde4ec", pt: 3, mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Sections
                <Typography component="span" fontSize="13px" color="text.secondary" ml={1}>
                  {totals.sections} · {totals.questions} questions
                </Typography>
              </Typography>
              <Typography fontSize="14px" color="text.secondary" mb={2}>
                Expand to review questions. EAO still gets the 52 fixed FR sections in their config screen.
              </Typography>
            </Box>
            {(framework?.sections || []).map((sec, i) => (
              <SectionBlock
                key={sec.id || i}
                section={sec}
                index={i}
                open={Boolean(openSections[i])}
                onToggle={() => setOpenSections((o) => ({ ...o, [i]: !o[i] }))}
                onEditSection={() => openEditSection(i)}
                onDeleteSection={() => confirmDeleteSection(i)}
                onEditQuestion={(qi) => openEditQuestion(i, qi)}
                onDeleteQuestion={(qi) => confirmDeleteQuestion(i, qi)}
                onAddQuestion={() => openAddQuestion(i)}
              />
            ))}
            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                p: 2,
                borderStyle: "dashed",
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography fontSize="13px" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
                Something not covered?
              </Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={openAddSection} sx={{ textTransform: "none" }}>
                Add section
              </Button>
            </Paper>
          </>
        )}

        {!isApplication && (
          <Box sx={{ borderTop: "1px solid #dde4ec", pt: 3, mt: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={1}>Client permissions</Typography>
            <Typography fontSize="14px" color="text.secondary" mb={2}>
              Controls whether the tenant EAO can edit framework structure (intake chat is always allowed).
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1, mb: 3 }}>
              {PERMISSION_OPTIONS.map((p) => (
                <Paper
                  key={p.value}
                  variant="outlined"
                  onClick={() => setPermissions(p.value)}
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    borderColor: permissions === p.value ? "primary.main" : "#dde4ec",
                    bgcolor: permissions === p.value
                      ? (theme) => alpha(theme.palette.primary.main, 0.08)
                      : "#fff",
                    boxShadow: permissions === p.value
                      ? (theme) => `inset 0 0 0 1px ${theme.palette.primary.main}`
                      : "none",
                  }}
                >
                  <Typography fontWeight={600} fontSize="14px">{p.label}</Typography>
                  <Typography fontSize="12px" color="text.secondary">{p.desc}</Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "rgba(255,255,255,0.95)",
          borderTop: "1px solid #dde4ec",
          px: 3,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography fontSize="13px" color="text.secondary" sx={{ flex: 1 }}>
          {templateStatus === "published" ? (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <CheckCircleOutlineIcon fontSize="small" color="primary" />
              {isApplication
                ? "Published — this application uses this template"
                : "Published — tenant apps use this template"}
            </Box>
          ) : (
            <>
              Draft — publish when ready for {tenant?.org_name || (isApplication ? "this application" : "tenant")}
            </>
          )}
        </Typography>
        <Button
          variant={isDirty ? "contained" : "outlined"}
          color="primary"
          onClick={handleSaveDraft}
          disabled={saving || !framework}
          sx={{ textTransform: "none" }}
        >
          Save draft
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handlePublish}
          disabled={saving || !framework?.sections?.length}
          sx={{ textTransform: "none" }}
        >
          {saving ? "Publishing…" : isApplication ? "Publish to Application" : "Publish to tenant"}
        </Button>
      </Box>

      <Dialog
        open={Boolean(sectionDialog)}
        onClose={() => !generatingQuestions && !similarityLoading && setSectionDialog(null)}
        maxWidth={sectionDialog?.mode === "add" ? "md" : "sm"}
        fullWidth
      >
        <DialogTitle>{sectionDialog?.mode === "add" ? "Add section" : "Edit section"}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mt: 1, mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Section title"
            value={sectionDialog?.name || ""}
            onChange={(e) => setSectionDialog((d) => ({ ...d, name: e.target.value }))}
            disabled={generatingQuestions}
            sx={{ mt: error ? 0 : 1, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Why this section matters"
            value={sectionDialog?.purpose || ""}
            onChange={(e) => setSectionDialog((d) => ({ ...d, purpose: e.target.value }))}
            disabled={generatingQuestions}
            sx={{ mb: sectionDialog?.mode === "add" ? 2 : 0 }}
          />

          {sectionDialog?.mode === "add" && (
            <Box>
              <Button
                variant="outlined"
                color="primary"
                startIcon={
                  generatingQuestions
                    ? <CircularProgress size={16} color="inherit" />
                    : <AutoAwesomeIcon />
                }
                onClick={handleGenerateQuestions}
                disabled={generatingQuestions || depth === "defaults" || !sectionDialog?.name?.trim()}
                sx={{ textTransform: "none", mb: 2 }}
              >
                {generatingQuestions ? "Generating…" : "Generate Questions"}
              </Button>

              <Typography fontWeight={600} fontSize="14px" mb={1}>
                Questions
                <Typography component="span" fontSize="12px" color="text.secondary" ml={1}>
                  {(sectionDialog.items || []).length}
                </Typography>
              </Typography>

              {(sectionDialog.items || []).length === 0 && !generatingQuestions && (
                <Typography fontSize="13px" color="text.secondary" fontStyle="italic" mb={1}>
                  No questions yet — generate with AI or add manually.
                </Typography>
              )}

              {(sectionDialog.items || []).map((q, qi) => (
                <Box
                  key={q.id || qi}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid #eef1f4",
                    bgcolor: "#f9fafb",
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label={`Question ${qi + 1}`}
                    value={q.text || ""}
                    onChange={(e) => updateDialogQuestion(qi, { text: e.target.value })}
                    disabled={generatingQuestions}
                    sx={{ mb: 0.5 }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={q.required !== false}
                          onChange={(e) => updateDialogQuestion(qi, { required: e.target.checked })}
                          disabled={generatingQuestions}
                        />
                      }
                      label="Required"
                    />
                    <IconButton
                      size="small"
                      title={
                        isRequiredQuestion(q)
                          ? "Required questions cannot be removed"
                          : "Remove question"
                      }
                      onClick={() => removeDialogQuestion(qi)}
                      disabled={generatingQuestions || isRequiredQuestion(q)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addDialogQuestion}
                disabled={generatingQuestions}
                sx={{ textTransform: "none" }}
              >
                Add Question
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSectionDialog(null)}
            disabled={similarityLoading || generatingQuestions}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveSectionDialog}
            disabled={!sectionDialog?.name?.trim() || similarityLoading || generatingQuestions}
            startIcon={similarityLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {similarityLoading
              ? "Checking…"
              : sectionDialog?.mode === "add"
                ? "Save & Close"
                : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(questionDialog)} onClose={() => setQuestionDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{questionDialog?.mode === "add" ? "Add question" : "Edit question"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Question"
            value={questionDialog?.text || ""}
            onChange={(e) => setQuestionDialog((d) => ({ ...d, text: e.target.value }))}
            sx={{ mt: 1, mb: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={questionDialog?.required !== false}
                onChange={(e) => setQuestionDialog((d) => ({ ...d, required: e.target.checked }))}
              />
            }
            label="Required"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuestionDialog(null)} disabled={similarityLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveQuestionDialog}
            disabled={!questionDialog?.text?.trim() || similarityLoading}
            startIcon={similarityLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {similarityLoading
              ? "Checking…"
              : questionDialog?.mode === "add"
                ? "Add question"
                : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteDialog)} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>{deleteDialog?.title}</DialogTitle>
        <DialogContent>
          <Typography fontSize="14px">{deleteDialog?.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => deleteDialog?.onConfirm?.()}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(similarityDialog)}
        onClose={() => setSimilarityDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        {similarityDialog && (
          <>
            <DialogTitle>{similarityDialog.title}</DialogTitle>
            <DialogContent>
              <Alert
                severity={
                  similarityDialog.severity === "block"
                    || similarityDialog.severity === "error"
                    || similarityDialog.severity === "hard_duplicate"
                    || similarityDialog.severity === "duplicate"
                    ? "error"
                    : "warning"
                }
                sx={{ mb: 2 }}
              >
                {similarityDialog.message
                  || (similarityDialog.severity === "hard_duplicate"
                    ? "This question is nearly identical to an existing one and cannot be added as written."
                    : similarityDialog.severity === "duplicate" || similarityDialog.severity === "block"
                      ? "These look nearly identical. Rewrite for clearer onboarding mapping, or override if intentional."
                      : "These may overlap in meaning. Review before continuing so intake answers map cleanly.")}
              </Alert>
              {(similarityDialog.matches || []).length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1 }}>
                  {similarityDialog.matches.map((m, i) => (
                    <Typography component="li" key={`m-${i}`} fontSize="13px" sx={{ mb: 1 }}>
                      <Chip
                        size="small"
                        label={`${m.score} · ${m.severity}`}
                        color={
                          m.severity === "block"
                            || m.severity === "hard_duplicate"
                            || m.severity === "duplicate"
                            ? "error"
                            : "warning"
                        }
                        sx={{ mr: 1, fontFamily: "monospace", fontSize: 11 }}
                      />
                      {formatMatchLine(m)}
                    </Typography>
                  ))}
                </Box>
              )}
              {(similarityDialog.conflicts || []).length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {similarityDialog.conflicts.slice(0, 12).map((c, i) => (
                    <Typography component="li" key={`c-${i}`} fontSize="13px" sx={{ mb: 1.25 }}>
                      <Chip
                        size="small"
                        label={`${c.score} · ${c.severity}`}
                        color={c.severity === "block" || c.severity === "hard_duplicate" ? "error" : "warning"}
                        sx={{ mr: 1, fontFamily: "monospace", fontSize: 11 }}
                      />
                      {formatConflictLine(c)}
                    </Typography>
                  ))}
                  {similarityDialog.conflicts.length > 12 && (
                    <Typography fontSize="12px" color="text.secondary">
                      …and {similarityDialog.conflicts.length - 12} more
                    </Typography>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSimilarityDialog(null)}>
                {similarityDialog.allowOverride === false ? "Close" : "Cancel"}
              </Button>
              {similarityDialog.allowOverride && similarityDialog.onProceed && (
                <Button
                  variant="contained"
                  color={
                    similarityDialog.severity === "block"
                      || similarityDialog.severity === "error"
                      || similarityDialog.severity === "duplicate"
                      || similarityDialog.severity === "hard_duplicate"
                      ? "error"
                      : "warning"
                  }
                  onClick={() => {
                    const proceed = similarityDialog.onProceed;
                    setSimilarityDialog(null);
                    proceed?.();
                  }}
                >
                  {similarityDialog.proceedLabel || "Keep anyway"}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={4000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity="success"
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(warning)}
        autoHideDuration={7000}
        onClose={() => setWarning("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity="warning"
          onClose={() => setWarning("")}
        >
          {warning}
        </Alert>
      </Snackbar>
    </Box>
  );
}
